import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import PropTypes from "prop-types";
import { CardHeader } from "./CardHeader";
import { DetailsModal } from "./DetailsModal";
import { useExportToXLS } from "../../hooks";
import { Box, Button, Grid, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircleLoader from "../Loaders/CircleLoader";
import {
  LoaderWrapper,
  CardContainer,
  CardContent,
  ExportButtonContainer,
  ActionsContainer,
} from "./styles";

function Card({
  app,
  children,
  header,
  style,
  className,
  expandable,
  detailButton,
  detailContent,
  cardContentStyle,
  exportTable,
  exportTableId,
  exportContainer,
}) {
  const notFullHd = useMediaQuery({ query: "(max-width: 1536px)" });
  const [openModal, setOpenModal] = useState(false);
  const mounted = useRef(true);

  const { exportToXLS, loadingExcel } = useExportToXLS(app);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleExport = () => {
    exportToXLS(exportTableId, exportContainer);
  };

  const Content = () => {
    const detailButton = React.Children.toArray(children).find(
      (child) => child?.props?.className === "detail-button"
    );
    const otherChildren = React.Children.toArray(children).filter(
      (child) => child?.props?.className !== "detail-button"
    );

    const renderActions = () => {
      if (!exportTable && !expandable && !detailButton) return null;

      return (
        <ActionsContainer>
          {exportTable && (
            <ExportButtonContainer>
              <Tooltip title="Exportar para Excel">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleExport}
                  endIcon={<FileDownloadOutlinedIcon />}
                  className="export-button"
                >
                  {loadingExcel ? (
                    <LoaderWrapper>
                      <CircleLoader />
                    </LoaderWrapper>
                  ) : null}
                </Button>
              </Tooltip>
              {detailButton}
            </ExportButtonContainer>
          )}

          {detailButton && !exportTable && (
            <Grid
              item
              className="cardRedirectButton primaryButton expandButton"
            >
              <Button
                variant="text"
                endIcon={<OpenInNewIcon />}
                onClick={() => setOpenModal(true)}
              >
                <Box component="span">Detalhes</Box>
              </Button>
            </Grid>
          )}
        </ActionsContainer>
      );
    };

    return (
      <CardContainer className={`${className} card`} style={style}>
        <CardContent className="cardContentArea" style={cardContentStyle}>
          <Grid
            container
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Grid
              className="cardHeader"
              item
              container
              justifyContent={header ? "space-between" : "flex-end"}
              alignItems="flex-start"
            >
              {header && (
                <CardHeader
                  {...header}
                  titleId={header.titleId || header.title?.id}
                  hardTitle={header.hardTitle || header.title?.hard}
                  subtitle={header.subtitle?.id}
                  hardSubtitle={header.subtitle?.hard}
                  app={app}
                />
              )}
              {renderActions()}
            </Grid>
            {otherChildren}
          </Grid>
        </CardContent>
      </CardContainer>
    );
  };

  if (detailButton && openModal) {
    return (
      <>
        <Content />
        <DetailsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          content={detailContent}
          exportTableId={exportTableId}
          onExport={handleExport}
          loadingExcel={loadingExcel}
          notFullHd={notFullHd}
        />
      </>
    );
  }

  return <Content />;
}

Card.defaultProps = {
  app: null,
  children: null,
  header: null,
  style: null,
  className: "",
  expandable: false,
  detailButton: false,
  absoluteExpandButton: false,
  cardContentStyle: {},
  exportTable: false,
  exportTableId: "",
  exportContainer: false,
};

Card.propTypes = {
  app: PropTypes.object,
  children: PropTypes.node,
  header: PropTypes.object,
  style: PropTypes.object,
  className: PropTypes.string,
  expandable: PropTypes.bool,
  detailButton: PropTypes.bool,
  absoluteExpandButton: PropTypes.bool,
  cardContentStyle: PropTypes.object,
  exportTable: PropTypes.bool,
  exportTableId: PropTypes.string,
  exportContainer: PropTypes.bool,
};

export default Card;
